import { html, ViewTemplate, ref, when } from '@microsoft/fast-element';
import type { VppaManagement } from './vppa-management';
import { plusIcon } from '../../../../styles';
import { PROFILE_RIGHTS } from '../../../../utils';
import { MORE_INFO_ITEMS } from '../../../../utils/enums/more-info.enum';
import { MoreInfoIconSize } from '../../../common/more-info/more-info-icon/more-info-icon.template';

export enum SELLER_DASHBOARD_GRID_TABS {
  TRADING_ACTIVITY_TAB = 'tradingActivity',
  TRADING_ACTIVITY_PANEL = 'tradingActivityPanel',
  ACTIVE_CONTRACTS_TAB = 'activeContracts',
  ACTIVE_CONTRACTS_PANEL = 'activeContractsPanel',
  INACTIVE_CONTRACTS_TAB = 'inactiveContracts',
  INACTIVE_CONTRACTS_PANEL = 'inactiveContractsPanel',
  NEGOTIATIONS_TAB = 'negotiations',
  NEGOTIATIONS_PANEL = 'negotiationsPanel',
  EXECUTED_TAB = 'executed',
  EXECUTED_PANEL = 'executedPanel',
  TERMINATED_TAB = 'terminated',
  TERMINATED_PANEL = 'terminatedPanel',
}

export const VppaManagementTemplate: ViewTemplate<VppaManagement> = html`
  <div class="container">
    <zero-tabs ${ref('vppaGridTabs')} activeId=${SELLER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB}>
      <!-- TABS -->
      ${when(
        (x) => !x.showOnlyNegotiations(),
        html`
          <zero-tab
            id=${SELLER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB}
            slot="tab"
            @click=${(x) => x.allTradingActivityVppa()}
          >
            Trading Activity
            <more-info-icon
              :isVisible=${(x) => x.vppaGridTabs.activeid == SELLER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB}
              :moreInfoItem=${() => MORE_INFO_ITEMS.SELLER_TRADING_ACTIVITY}
              :size=${() => MoreInfoIconSize.SMALL}
            ></more-info-icon>
          </zero-tab>
          <zero-tab
            id=${SELLER_DASHBOARD_GRID_TABS.ACTIVE_CONTRACTS_TAB}
            slot="tab"
            @click=${(x) => x.allActiveVppa()}
          >
            Active Contracts
            <more-info-icon
              :isVisible=${(x) => x.vppaGridTabs.activeid == SELLER_DASHBOARD_GRID_TABS.ACTIVE_CONTRACTS_TAB}
              :moreInfoItem=${() => MORE_INFO_ITEMS.ACTIVE_CONTRACTS}
              :size=${() => MoreInfoIconSize.SMALL}
            ></more-info-icon>
          </zero-tab>
          <zero-tab
            id=${SELLER_DASHBOARD_GRID_TABS.INACTIVE_CONTRACTS_TAB}
            slot="tab"
            @click=${(x) => x.allInactiveVppa()}
          >
            Inactive Contracts
            <more-info-icon
              :isVisible=${(x) => x.vppaGridTabs.activeid == SELLER_DASHBOARD_GRID_TABS.INACTIVE_CONTRACTS_TAB}
              :moreInfoItem=${() => MORE_INFO_ITEMS.INACTIVE_CONTRACTS}
              :size=${() => MoreInfoIconSize.SMALL}
            ></more-info-icon>
          </zero-tab>
        `,
      )}
      <zero-tab
        id=${SELLER_DASHBOARD_GRID_TABS.NEGOTIATIONS_TAB}
        slot="tab"
        @click=${(x) => x.allVppaNegotiations()}
      >
        Negotiations
        <more-info-icon
          :isVisible=${(x) => x.vppaGridTabs.activeid == SELLER_DASHBOARD_GRID_TABS.NEGOTIATIONS_TAB}
          :moreInfoItem=${() => MORE_INFO_ITEMS.NEGOTIATIONS}
          :size=${() => MoreInfoIconSize.SMALL}
        ></more-info-icon>
      </zero-tab>
      <zero-tab
        id=${SELLER_DASHBOARD_GRID_TABS.EXECUTED_TAB}
        slot="tab"
        @click=${(x) => x.allExecutedVppa()}
      >
        Executed
        <more-info-icon
          :isVisible=${(x) => x.vppaGridTabs.activeid == SELLER_DASHBOARD_GRID_TABS.EXECUTED_TAB}
          :moreInfoItem=${() => MORE_INFO_ITEMS.EXECUTED}
          :size=${() => MoreInfoIconSize.SMALL}
        ></more-info-icon>
      </zero-tab>
      ${when(
        (x) => !x.showOnlyNegotiations(),
        html`
          <zero-tab
            id=${SELLER_DASHBOARD_GRID_TABS.TERMINATED_TAB}
            slot="tab"
            @click=${(x) => x.allTerminatedVppa()}
          >
            Terminated
            <more-info-icon
              :isVisible=${(x) => x.vppaGridTabs.activeid == SELLER_DASHBOARD_GRID_TABS.TERMINATED_TAB}
              :moreInfoItem=${() => MORE_INFO_ITEMS.TERMINATED}
              :size=${() => MoreInfoIconSize.SMALL}
            ></more-info-icon>
          </zero-tab>
        `,
      )}

      <!-- PANELS -->
      ${when(
        (x) => !x.showOnlyNegotiations(),
        html`
          <zero-tab-panel
            id=${SELLER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_PANEL}
            slot="tabpanel"
          ></zero-tab-panel>
          <zero-tab-panel
            id=${SELLER_DASHBOARD_GRID_TABS.ACTIVE_CONTRACTS_PANEL}
            slot="tabpanel"
          ></zero-tab-panel>
          <zero-tab-panel
            id=${SELLER_DASHBOARD_GRID_TABS.INACTIVE_CONTRACTS_PANEL}
            slot="tabpanel"
          ></zero-tab-panel>
        `,
      )}
      <zero-tab-panel
        id=${SELLER_DASHBOARD_GRID_TABS.NEGOTIATIONS_PANEL}
        slot="tabpanel"
      ></zero-tab-panel>
      <zero-tab-panel
        id=${SELLER_DASHBOARD_GRID_TABS.EXECUTED_PANEL}
        slot="tabpanel"
      ></zero-tab-panel>
      ${when(
        (x) => !x.showOnlyNegotiations(),
        html`
          <zero-tab-panel
            id=${SELLER_DASHBOARD_GRID_TABS.TERMINATED_PANEL}
            slot="tabpanel"
          ></zero-tab-panel>
        `,
      )}

      <tab-buttons slot="end">
        <zero-button
          class="buttons-padding"
          slot="buttons"
          appearance="tab-primary"
          @click=${(x) => x.downloadGridCsv()}
        >
          Download
        </zero-button>
        ${when(
          (x) => x.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.UPSERT_VPPA),
          html<VppaManagement>`
            <zero-button
              slot="buttons"
              id="newContract"
              appearance="tab-primary"
              ?disabled=${(x) => !x.hasProject}
              @click=${(x) => x.openVppaModal()}
            >
              ${plusIcon('start')} New Contract
            </zero-button>
          `,
        )}
      </tab-buttons>
    </zero-tabs>
    <zero-tooltip anchor="newContract" position="left" ?hidden=${(x) => x.hasProject}>
      Please select a Project tile in My Projects
    </zero-tooltip>
    <all-vppas
      class="vppa-grid"
      :activeTabId=${(x) => x.vppaGridTabs.activeid}
      :projectId=${(x) => x.projectId}
      :projectListEmpty=${(x) => x.projectsListEmpty}
      ${ref('allVppa')}
      style="display: ${(x) => (x.isNegotiationTabSelected ? 'none' : '')}"
    ></all-vppas>
    <negotiation-seller-dashboard
      class="vppa-grid"
      :activeTabId=${(x) => x.vppaGridTabs.activeid}
      :projectId=${(x) => x.projectId}
      :projectListEmpty=${(x) => x.projectsListEmpty}
      ${ref('negotiationGrid')}
      style="display: ${(x) => (x.isNegotiationTabSelected ? '' : 'none')}"
    ></negotiation-seller-dashboard>
  </div>
`;
